import { useEffect } from 'react';
import { useCallback } from 'react';
import { useEvent, useUnit } from 'effector-react';
import {
  ManageMembersContext,
  ManageMembersOrgLimits,
  ManageMembersType,
  ManageMembersView,
  MenuItem,
} from '@nx-frontend/shared/types';
import ManageMembersContent from './manage-members-content';
import ManageMembersHeader from './manage-members-header';
import {
  $isFetching,
  resetManageMembersEvent,
  updateContextEvent,
  updateForceIsOnlySharedAvailableEvent,
  updateMenuItemEvent,
  updateOrgLimitsEvent,
  updateOrgMaxMembersEvent,
  updateTypeEvent,
  updateViewModeEvent,
} from './store';

export interface ManageMembersProps {
  type: ManageMembersType;
  initialViewMode: ManageMembersView;
  context: ManageMembersContext;
  menuItem?: MenuItem;
  orgMaxMembers: number;
  orgLimits: ManageMembersOrgLimits;
  forceIsOnlySharedAvailable?: boolean;
  onClose?: () => void;
}

export function ManageMembers({
  type,
  initialViewMode,
  context,
  menuItem,
  orgMaxMembers,
  orgLimits,
  forceIsOnlySharedAvailable,
  onClose,
}: ManageMembersProps) {
  const [isFetching] = useUnit([$isFetching]);
  const [
    updateViewMode,
    updateOrgMaxMembers,
    updateMenuItem,
    updateContext,
    updateOrgLimits,
    updateType,
    resetManageMembers,
    updateForceIsOnlySharedAvailable,
  ] = useEvent([
    updateViewModeEvent,
    updateOrgMaxMembersEvent,
    updateMenuItemEvent,
    updateContextEvent,
    updateOrgLimitsEvent,
    updateTypeEvent,
    resetManageMembersEvent,
    updateForceIsOnlySharedAvailableEvent,
  ]);

  useEffect(() => {
    updateType(type);
  }, [type, updateType]);

  useEffect(() => {
    updateViewMode(initialViewMode);
  }, [initialViewMode, updateViewMode]);

  useEffect(() => {
    updateOrgMaxMembers(orgMaxMembers);
  }, [orgMaxMembers, updateOrgMaxMembers]);

  useEffect(() => {
    updateContext(context);
  }, [context, updateContext]);

  useEffect(() => {
    if (menuItem) {
      updateMenuItem(menuItem);
    }
  }, [menuItem, updateMenuItem]);

  useEffect(() => {
    updateOrgLimits(orgLimits);
  }, [orgLimits, updateOrgLimits]);

  useEffect(() => {
    updateForceIsOnlySharedAvailable(Boolean(forceIsOnlySharedAvailable));
  }, [forceIsOnlySharedAvailable, updateForceIsOnlySharedAvailable]);

  const handleClose = useCallback(() => {
    resetManageMembers();
    if (onClose) {
      onClose();
    }
  }, [onClose, resetManageMembers]);

  return (
    <div className="w-[800px] h-[500px] flex flex-col">
      <ManageMembersHeader onClose={handleClose} />
      <div className="mt-3 min-h-0 flex flex-col h-full text-sm">
        {!isFetching && <ManageMembersContent />}
        {isFetching && (
          <svg width="800" height="460" viewBox="0 0 800 460" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="800" height="460" fill="white" />
            <rect x="4" width="77" height="24" rx="12" fill="#E4E4E7" />
            <rect x="93" width="116" height="24" rx="12" fill="#F5F5F5" />
            <rect x="4" y="44" width="111" height="28" rx="4" fill="#F5F5F5" />
            <rect x="125" y="44" width="118.5" height="28" rx="4" fill="#F5F5F5" />
            <rect x="253.5" y="44" width="268.25" height="28" rx="4" fill="#F5F5F5" />
            <rect x="531.75" y="44" width="268.25" height="28" rx="4" fill="#F5F5F5" />
            <rect x="4" y="92" width="111" height="28" rx="4" fill="#F5F5F5" />
            <rect x="125" y="92" width="118.5" height="28" rx="4" fill="#F5F5F5" />
            <rect x="253.5" y="92" width="268.25" height="28" rx="4" fill="#F5F5F5" />
            <rect x="531.75" y="92" width="268.25" height="28" rx="4" fill="#F5F5F5" />
            <rect x="4" y="140" width="69" height="28" rx="4" fill="#E4E4E7" />
            <rect x="651" y="433" width="67" height="26" rx="3" stroke="#E4E4E7" strokeWidth="2" />
            <rect x="731" y="432" width="69" height="28" rx="4" fill="#E4E4E7" />
          </svg>
        )}
      </div>
    </div>
  );
}

export default ManageMembers;
